import React, { useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import Separator from '../separator';
import { TooltipIcon } from '../../helpers/Styled';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Col, Row, Tooltip } from '@sumup/circuit-ui/legacy';
import { css } from '@emotion/react';
import { formatCurrency } from '../../utils/formatCurrency';

const StyledTooltipSmall = styled(Tooltip)`
	width: 165px;
`;

const TooltipContainerSmall = styled.span(({ theme }) => css`
	height: ${theme.iconSizes.kilo};
	position: absolute;
	margin-left: ${theme.spacings.bit};
	width: 20px;
`);

const PriceSummaryRow = styled(Row)`
	display: flex;
	flex-flow: row nowrap;
	align-items: start;
	margin-bottom: ${props => props.theme.spacings.bit};
`;

const PriceSummaryNameCol = styled(Col)`
	flex: 1;
`;

const PriceSummaryAmountCol = styled(Col)`
	flex: 1;
	white-space: nowrap;
	text-align: right;
	width: auto;
`;

const TooltipContainer = ({ text }) => {
	const [tooltipVisible, setTooltipVisible] = useState(false);
	return <TooltipContainerSmall data-testid="refund-tooltip">
		{ tooltipVisible && (
			<StyledTooltipSmall position="top" align="right">
				{text}
			</StyledTooltipSmall>)
		}
		<TooltipIcon
			onMouseEnter={() => setTooltipVisible(true)}
			onMouseLeave={() => setTooltipVisible(false)}
		/>
	</TooltipContainerSmall>;
};

const proRataTooltip = (days) => <TooltipContainer text={i18n.t('summary.subscription.refund.tooltip', { days })} />;
const discountTooltip = (discountRate, couponCode, discountAmount, currency) => discountRate ? (
	<TooltipContainer text={i18n.t('coupon_code.tooltip', { discountRate, couponCode })} />
) : (
	<TooltipContainer text={i18n.t('coupon_code.tooltip_amount', { discountAmount: formatCurrency(discountAmount, currency), couponCode })} />
);

const SALES_TAX_TYPE = 'sales_tax';

const roundRate = (rate) => Math.round((rate ?? 0) * 100) / 100;

const PriceSummaryLine = ({ line, planFrequency, couponCode, isTrialApplicable }) => {
	const { t } = useTranslation();

	const shouldSkipTaxLine = line.type === 'tax' && line.taxType === 'none';
	if (shouldSkipTaxLine) {
		return null;
	}

	const getNetText = () => {
		if (isTrialApplicable) {
			return t('summary.trial_period');
		}
		const frequencyLabel = planFrequency === 'monthly'
			? t('summary.plan.frequency.month.title')
			: t('summary.plan.frequency.year.title');
		return `1 ${frequencyLabel}`;
	};

	const getTaxText = () => {
		const taxLabel = line.taxType === SALES_TAX_TYPE ? t('sales_tax.summary') : t('vat.summary');
		return `${taxLabel} ${roundRate(line.rate)}%`;
	};

	const getText = () => {
		switch (line.type) {
			case 'net': return getNetText();
			case 'tax': return getTaxText();
			case 'refund': return t('summary.prorata');
			case 'subtotal': return t('summary.subtotal');
			case 'total': return t('summary.total');
			case 'discount': return t('summary.discount');
			default: return '';
		}
	};
	const text = getText();

	const isLineTypeTotal = line.type === 'total';
	const isLineTypeSubtotal = line.type === 'subtotal';
	const isEmphasized = isLineTypeTotal || isLineTypeSubtotal;

	const amount = isTrialApplicable && isLineTypeTotal ? 0 : line.amount;
	const amountWithCurrency = formatCurrency(amount, line.currency);

	const renderSeparatorBeforeLine = isEmphasized;
	const fontWeight = isEmphasized ? 'bold' : 'normal';
	const fontSize = isLineTypeTotal && line.refundDaysAmount ? '1.3em' : '1em';
	const textDecoration = isTrialApplicable && !isLineTypeTotal ? 'line-through' : 'none';

	return (
		<>
			{renderSeparatorBeforeLine && <Separator />}
			<PriceSummaryRow style={{ fontWeight }} data-testid='price-summary-line'>
				<PriceSummaryNameCol>{text}
					{line.type === 'refund' && proRataTooltip(line.refundDaysAmount)}
					{line.type === 'discount' && discountTooltip(line.rate, couponCode, -line.amount, line.currency)}
				</PriceSummaryNameCol>
				<PriceSummaryAmountCol style={{ fontSize, textDecoration }}>{amountWithCurrency}</PriceSummaryAmountCol>
			</PriceSummaryRow>
		</>
	);
};

PriceSummaryLine.propTypes = {
	planFrequency: PropTypes.string.isRequired,
	line: PropTypes.shape({
		type: PropTypes.string.isRequired,
		amount: PropTypes.number.isRequired,
		currency: PropTypes.string.isRequired,
		rate: PropTypes.number,
		taxType: PropTypes.string,
		refundDaysAmount: PropTypes.number
	}).isRequired,
	isTrialApplicable: PropTypes.bool.isRequired,
};

export default PriceSummaryLine;
